/**
 * InviteUserModal
 *
 * Modal for inviting a new user by name, email, and entity context.
 * Uses React Hook Form + Zod for validation and ControlledFormItem for form fields.
 * The entity field is read-only and displays the current business account.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/organisms/InviteUserModal/`
 *
 * ## Features
 * - Full name field (required, min 2 characters via Zod)
 * - Email field (required, email format validated via Zod)
 * - Read-only entity field showing current business account
 * - Calls POST /users/invite and invalidates the invites cache on success
 *
 * @module features/users/components/ui/organisms/InviteUserModal
 */
import React, { useCallback } from 'react';
import { App, Modal, Form, Button, Select, Space } from 'antd';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';

import { useLanguage } from '@/shared/hooks/useLanguage';
import { ControlledFormItem } from '@/shared/components/ui/atoms';
import { createUserInvite } from '@/features/users/services/userInvites';
import { USER_INVITES_QUERY_KEY } from '@/features/users/hooks/useUserInvites';

import type { InviteFormData, InviteUserModalProps } from './types';
import './InviteUserModal.css';

/**
 * InviteUserModal Component
 *
 * @param props - Component properties
 * @returns React component
 *
 * @example
 * ```tsx
 * <InviteUserModal
 *   open={isOpen}
 *   entityName="Empresa LTDA"
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => console.log('Invite sent')}
 * />
 * ```
 */
export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  entityName,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const inviteSchema = z.object({
    email: z
      .string()
      .min(1, t('Please enter a valid email'))
      .email(t('Invalid email format')),
    fullName: z.string().min(2, t('Name must be at least 2 characters')),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      fullName: '',
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  /**
   * Handles form submission after Zod validation passes.
   * Creates the invite, invalidates the cache, and closes the modal.
   */
  const handleFormSubmit = useCallback(
    async (data: InviteFormData) => {
      try {
        await createUserInvite({
          email: data.email.trim(),
          fullName: data.fullName.trim(),
        });

        await queryClient.invalidateQueries({
          queryKey: USER_INVITES_QUERY_KEY,
        });

        message.success(t('Invite sent'));
        reset();
        onSuccess?.();
        onClose();
      } catch {
        message.error(t('Failed to send invite'));
      }
    },
    [queryClient, message, t, onSuccess, onClose, reset]
  );

  return (
    <Modal
      open={open}
      title={t('Invite user')}
      onCancel={handleClose}
      aria-label={t('Invite user modal')}
      destroyOnHidden
      className="invite-user-modal"
      footer={null}
      width={700}
    >
      <Form
        layout="vertical"
        onFinish={handleSubmit(handleFormSubmit)}
        noValidate
        className="invite-user-modal__form"
      >
        <div className="invite-user-modal__form-container">
          <ControlledFormItem
            name="fullName"
            control={control as never}
            errors={errors}
            label={t('Name')}
            inputType="text"
            inputProps={{
              placeholder: t('Enter the new user name'),
              autoComplete: 'name',
            }}
          />

          <ControlledFormItem
            name="email"
            control={control as never}
            errors={errors}
            label={t('Email')}
            inputType="email"
            inputProps={{
              placeholder: t('Enter the new user email'),
              autoComplete: 'email',
            }}
          />

          <Form.Item
            label={t('Entity')}
            className="invite-user-modal__entity-field"
          >
            <Select
              value={entityName ?? t('Current entity')}
              disabled
              className="invite-user-modal__entity-select"
              suffixIcon={
                <span className="invite-user-modal__chevron-down">&#8964;</span>
              }
            />
          </Form.Item>
        </div>

        <Form.Item className="invite-user-modal__footer">
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              className="invite-user-modal__close-btn"
            >
              {t('Close')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              className="invite-user-modal__save-btn"
            >
              {t('Save')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InviteUserModal;
